import { BitmapText, Container } from 'pixi.js'
import { useGameStore } from '../../game/store/GameStore'
import { gsap } from 'gsap'
import { spinService } from '../../game'
import { symbolUtil } from '../../spineManager'
import { SymbolType } from '../../game/enum/SymbolType'
import { BombSymbolType } from '../../spineManager/enum/animationType'
import { changeNode, sleep } from '../../game/util/gameUtil'
import { gameConfig } from '../../../config'
import { multiplierService } from '../../spin/service/MultiplierService'
import { freeGameService } from '../../freeGame/service/freeGameService'
import { soundManager } from '@slot-nx/core'
import { SweetBombSoundType } from '../../soundManager/enum/soundType'
import { SlotSymbolInfo } from '../../placeOrder/api/PlaceOrderResponse'

export interface WinningSymbolType {
  node: Container
  moveParameters: number
  nweSymbolMultiplier: number
  newSymbol?: number
}

export class ResultService {
  public singlePayoutNode: Container
  private eliminationNodes: WinningSymbolType[][] = []
  private preservationNodes: WinningSymbolType[][] = []
  private remainTable: SlotSymbolInfo[][] = []

  public saveRemainTable() {
    const reelNodes = spinService.reelNodes
    this.remainTable = []

    reelNodes.forEach((reel) => {
      const reelSymbols: { sweetBombSymbol: SlotSymbolInfo, positionY: number }[] = []

      reel.children.slice().forEach((symbol) => {
        const symbolName = Number(symbol.name)
        const symbolt = symbol as Container
        const multiplier = Number((symbolt.getChildAt(1) as BitmapText).text.slice(1))

        const sweetBombSymbol: SlotSymbolInfo = {
          symbol: symbolName,
          multiplier: multiplier,
        }

        reelSymbols.push({
          sweetBombSymbol,
          positionY: symbolt.y,
        })
      })
      reelSymbols.sort((a, b) => b.positionY - a.positionY)
      this.remainTable.push(reelSymbols.map(item => item.sweetBombSymbol))
    })
  }

  public setRemainTable() {
    const reelNodes = spinService.reelNodes
    reelNodes.forEach((reel, reelIndex) => {
      reel.children.sort((a, b) => {
        return a.position.y - b.position.y
      })
      reel.children
        .slice()
        .reverse()
        .forEach((symbol, symbolIndex) => {
          const symbolContainer = symbol as Container
          changeNode(symbolContainer, this.remainTable[reelIndex][symbolIndex])
        })
    })
  }

  public onWinningSymbolHandle() {
    this.eliminationNodes = []
    this.preservationNodes = []
    const currentDropResult = useGameStore.getState().currentDropTable
    const winningSymbolData = useGameStore.getState().currentWinningReel
    const winningSymbols = winningSymbolData.map(reel => ({ symbol: reel.symbol }))
    const reelNodes = spinService.reelNodes

    reelNodes.forEach((reel, reelIndex) => {
      const reelEliminationData: WinningSymbolType[] = []
      const reelPreservationData: WinningSymbolType[] = []
      reel.children.sort((a, b) => {
        return a.position.y - b.position.y
      })
      reel.children
        .slice()
        .reverse()
        .forEach((symbol) => {
          const currentSymbol = Number(symbol.name) as unknown as SymbolType
          const reelEliminationLength = reelEliminationData.length

          if (
            winningSymbols.some(item => currentSymbol === item.symbol)
            || currentSymbol === SymbolType.Multiplier
          ) {
            reelEliminationData.push({
              node: symbol as Container,
              moveParameters: reelEliminationLength + 1,
              nweSymbolMultiplier: currentDropResult[reelIndex][reelEliminationLength].multiplier,
              newSymbol: currentDropResult[reelIndex][reelEliminationLength].symbol,
            })
          }
          else {
            reelPreservationData.push({
              node: symbol as Container,
              moveParameters: reelEliminationLength,
              nweSymbolMultiplier: 0,
            })
          }
        })

      this.eliminationNodes.push(reelEliminationData)
      this.preservationNodes.push(reelPreservationData)
    })
  }

  public async showBombAnimation() {
    const sourceReelContainer = spinService.reelNodeRef
    const displayReelContainer = spinService.reelDisplayNodeRef

    this.moveChildContainer(sourceReelContainer, displayReelContainer)
    const animations: Promise<void>[] = []

    const playSound = new Set()
    this.eliminationNodes.forEach((reel) => {
      reel.forEach(async (symbolInfo) => {
        if (symbolInfo.node.name === SymbolType.Multiplier.toString()) {
          if (!playSound.has(symbolInfo.node.name)) {
            playSound.add(symbolInfo.node.name)
            // play once
            soundManager.play(SweetBombSoundType.BombExplode)
          }
          animations.push(this.onBombHandle(symbolInfo))
        }
      })
    })

    await Promise.all(animations)

    this.moveChildContainer(displayReelContainer, sourceReelContainer)
  }

  private moveChildContainer(source: Container, destination: Container) {
    let movedChildren = []
    if (source && destination) {
      movedChildren = [...source.children]
      movedChildren.forEach((child) => {
        source.removeChild(child)
        destination.addChild(child)
      })
    }
  }

  private async onBombHandle(symbolInfo: WinningSymbolType) {
    const multiplier = Number((symbolInfo.node.getChildAt(1) as BitmapText).text.slice(1))
    const bimapTextNode = symbolInfo.node.getChildAt(1)
    await symbolUtil.bombSymbolAnimation({
      symbolInfo,
      animationName: BombSymbolType.BombSymbolTypeNormal,
      isLoop: false,
      textEventName: BombSymbolType.BombSymbolTypeAction,
      async textAnimation() {
        gsap.timeline()
          .to(bimapTextNode.scale, { duration: 0.5, x: 1.5, y: 1.5, ease: 'power2.out', onComplete: () => {
            multiplierService.addBombNumToMultiplier(multiplier)
          } })
          .to(bimapTextNode.scale, { duration: 0.1, x: 1.4, y: 1.4, ease: 'power2.in' })
          .to(bimapTextNode, { duration: 0, alpha: 0, scaleX: 1, scaleY: 1 })
      },
    })
    this.symbolMoveUp({ symbolInfo })
    symbolInfo.node.getChildAt(1).alpha = 1
  }

  private symbolMoveUp({ symbolInfo }: { symbolInfo: WinningSymbolType }) {
    symbolInfo.node.y = -gameConfig.nodeHeight * symbolInfo.moveParameters
  }

  public async showSymbolWinning() {
    const animations: Promise<void>[] = []

    this.eliminationNodes.forEach((reel) => {
      reel.forEach(async (symbolInfo, symbolInfoIndex) => {
        if (reel.length - 1 === symbolInfoIndex) {
          soundManager.play(SweetBombSoundType.CandyMachine)
        }
        const task = symbolUtil.symbolWin({ symbolInfo })
        animations.push(task)
      })
    })

    await Promise.all(animations)
  }

  public async showSymboLoop() {
    const animations: Promise<void>[] = []

    this.eliminationNodes.forEach((reel) => {
      reel.forEach(async (symbolInfo) => {
        const task = symbolUtil.symbolLoop({ symbolInfo })
        animations.push(task)
      })
    })

    await Promise.all(animations)
  }

  public async showSymbolEliminate() {
    const animations: Promise<void>[] = []
    soundManager.play(SweetBombSoundType.SymEliminate)
    this.eliminationNodes.forEach((reel) => {
      reel.forEach(async (symbolInfo) => {
        const task = symbolUtil.symbolEliminate({ symbolInfo })
        animations.push(task)
      })
    })
    await Promise.all(animations)
  }

  public async removeSpine() {
    const animations: Promise<void>[] = []

    this.eliminationNodes.forEach((reel) => {
      reel.forEach(async (symbolInfo) => {
        const task = symbolUtil.removeSpine({ symbolInfo })
        animations.push(task)
      })
    })
    await Promise.all(animations)
  }

  public async onDropHandle() {
    const reelSymbolDropPromise = this.reelSymbolDrop()
    if (useGameStore.getState().isPeek()) {
      await reelSymbolDropPromise
      await this.checkPeekScatter()
      await this.newSymbolDrop()
    }
    else {
      const newSymbolDropPromise = this.newSymbolDrop()
      await Promise.all([reelSymbolDropPromise, newSymbolDropPromise])
    }
  }

  private async reelSymbolDrop() {
    const animations: Promise<void>[] = []

    this.preservationNodes.forEach((reel) => {
      if (reel.length > 0) {
        soundManager.play(SweetBombSoundType.Stop)
        reel.forEach((symbolInfo, symbolIndex) => {
          const task = symbolUtil.dropSymbol({ symbolInfo, dropSpace: symbolInfo.moveParameters, symbolIndex, isNewSymbol: false })
          animations.push(task)
        })
      }
    })

    await Promise.all(animations)
  }

  private async newSymbolDrop() {
    const animations: Promise<void>[] = []
    const isThisSpinInSpeedMode = useGameStore.getState().isThisSpinInSpeedMode

    for (const reel of this.eliminationNodes) {
      if (reel.length > 0) {
        await sleep((isThisSpinInSpeedMode ? gameConfig.speedModeReelDelay : gameConfig.reelDelay) * 1000)
        soundManager.play(SweetBombSoundType.Stop)
        reel.forEach((symbolInfo, symbolIndex) => {
          // change symbol
          changeNode(symbolInfo.node, {
            symbol: symbolInfo.newSymbol as number,
            multiplier: symbolInfo.nweSymbolMultiplier,
          })

          // drop symbol
          const task = symbolUtil.dropSymbol({ symbolInfo, dropSpace: reel.length, symbolIndex, isNewSymbol: true })
          animations.push(task)
        })
      }
    }

    await Promise.all(animations)
  }

  private async checkPeekScatter() {
    const isPeek = useGameStore.getState().isPeek()
    if (!isPeek) return
    await this.openPeekScatterAnimation()
  }

  private async openPeekScatterAnimation() {
    const allAnimationPromise = [] as Promise<void>[]
    this.preservationNodes.forEach((reels) => {
      let animationPromise: Promise<void>
      reels.forEach((symbol) => {
        if (Number(symbol.node.name) === SymbolType.Bonus) {
          animationPromise = symbolUtil.scatterPeek({ node: symbol.node, isSuper: false })
        }
        else if (Number(symbol.node.name) === SymbolType.SuperBonus) {
          animationPromise = symbolUtil.scatterPeek({ node: symbol.node, isSuper: true })
        }

        allAnimationPromise.push(animationPromise)
      })
    })

    await Promise.all(allAnimationPromise)
  }

  public async checkWinScatter() {
    const isWinScatter = useGameStore.getState().isWinScatter()
    if (isWinScatter) {
      await this.openWinScatterAnimation()
      if (useGameStore.getState().isFreeGame) {
        await freeGameService.increaseFreeGameCount()
        useGameStore.setState(state => ({
          freeGameCount: state.freeGameCount + state.bonusFreeGameCount,
        }))
      }
    }
  }

  private async openWinScatterAnimation() {
    const reelNodes = spinService.reelNodes
    const allAnimationPromise = [] as Promise<void>[]
    let isSuper = false
    reelNodes.forEach((reels) => {
      let animationPromise: Promise<void>
      reels.children.forEach((symbol) => {
        if (Number(symbol.name) === SymbolType.Bonus) {
          animationPromise = symbolUtil.scatterWin({ node: symbol as Container, isSuper: false })
        }
        else if (Number(symbol.name) === SymbolType.SuperBonus) {
          isSuper = true
          animationPromise = symbolUtil.scatterWin({ node: symbol as Container, isSuper: true })
        }

        allAnimationPromise.push(animationPromise)
      })
    })
    if (isSuper) {
      soundManager.play(SweetBombSoundType.Scatter2Trigger)
    }
    else {
      soundManager.play(SweetBombSoundType.Scatter1Trigger)
    }
    await Promise.all(allAnimationPromise)
  }
}
